import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useField } from 'react-final-form';
import { InputField as BaseInputField, SelectField, TimeField } from '@ubeya/shared-web/components';
import { responsiveFields } from '@ubeya/shared-web/style';
import { required } from '@ubeya/shared/utils/form';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';

const Container = styled.div`
  ${responsiveFields.mobile};
`;

const InputField = styled(BaseInputField)`
  input {
    border-radius: 5px;
    line-height: 25px;
  }
`;

const RuleInfo = styled.div`
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin-bottom: 16px;
  font-size: 14px;
  color: #495057;

  strong {
    color: #212529;
    font-weight: 600;
  }
`;

const BreakModalFields = ({ t, date, breakTypes, actualIn, selectedBreak }) => {
  const {
    input: { value: startTimeField, onChange: onChangeStartTime }
  } = useField('startTime');

  const {
    input: { value: endTimeField, onChange: onChangeEndTime }
  } = useField('endTime');

  const {
    input: { onChange: onChangeBreakDuration }
  } = useField('breakDuration');

  const complianceRule = selectedBreak?.complianceRule;
  const complianceRuleId = selectedBreak?.complianceRuleId;

  const handleTimeChange = useCallback(
    (value, field) => {
      const { startTime, endTime } = getTimeRangeWithDates({
        date,
        startTime: field === 'start' ? value : startTimeField,
        endTime: field === 'end' ? value : endTimeField
      });

      if (startTime && endTime) {
        const duration = moment.duration(moment(endTime).diff(startTime, 'milliseconds')).asMinutes();
        onChangeBreakDuration(duration);
      }
    },
    [date, endTimeField, onChangeBreakDuration, startTimeField]
  );

  const handleDurationChange = useCallback(
    (duration) => {
      onChangeBreakDuration(duration);
      const newEndTime = moment(actualIn).add(duration, 'minutes').format(API_DATE_TIME_FORMAT);
      onChangeStartTime(actualIn);
      onChangeEndTime(newEndTime);
    },
    [onChangeBreakDuration, onChangeEndTime, onChangeStartTime, actualIn]
  );

  const breakTypesOptions = useMemo(
    () =>
      (breakTypes || []).filter(({ isDeleted }) => !isDeleted).map((type) => ({ value: type.id, label: type.title })),
    [breakTypes]
  );

  const breakTypesDeletedOptions = useMemo(
    () =>
      (breakTypes || [])
        .filter(({ isDeleted }) => !!isDeleted)
        .map((type) => ({
          value: type.id,
          label: `${type.title}
      (${t('deleted')})`
        })),
    [t, breakTypes]
  );

  const ruleDisplayName = useMemo(() => {
    if (!complianceRuleId || !complianceRule) {
      return null;
    }
    const ruleName = complianceRule.name || '';
    const ruleSetName = complianceRule.ruleSet?.name || '';
    return ruleSetName ? `${ruleName} (${ruleSetName})` : ruleName;
  }, [complianceRule, complianceRuleId]);

  return (
    <Container>
      {ruleDisplayName && (
        <RuleInfo>
          <strong>{t('ruleName')}:</strong> {ruleDisplayName}
        </RuleInfo>
      )}
      <TimeField
        name="startTime"
        onChange={(newTime) => {
          onChangeStartTime(newTime);
          handleTimeChange(newTime, 'start');
        }}
        title={t('from')}
        defaultValue={actualIn}
        usePortal
        validate={required}
      />
      <TimeField
        name="endTime"
        title={t('to')}
        defaultValue={actualIn}
        isDisabled={!startTimeField}
        onChange={(newTime) => {
          onChangeEndTime(newTime);
          handleTimeChange(newTime, 'end');
        }}
        validate={required}
        usePortal
      />
      <InputField
        type="number"
        name="breakDuration"
        controlledValue
        onChange={handleDurationChange}
        title={t('breakDurationInMinutes')}
      />

      {breakTypesOptions?.length > 0 && (
        <SelectField
          name="breakTypeId"
          title={t('breakType')}
          options={breakTypesOptions}
          isMulti={false}
          clearable
          displayInitialValueNotInOptions={breakTypesDeletedOptions}
          usePortal
        />
      )}

      <InputField name="comments" title={t('comment')} className="full" />
    </Container>
  );
};

export default BreakModalFields;
